import React, { useEffect, useMemo, useRef, useState } from 'react';
import { IconButton } from '@mui/material';
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import CloseIcon from '@mui/icons-material/Close';
import XtramModal, {
    XtramButton,
    XtramCheckbox,
    XtramDatePicker,
    XtramField,
    XtramInput,
    XtramNotice,
    XtramSelect,
} from '../../Widgets/XtramModal/XtramModal.jsx';
import styles from './OtherDocumentsXtram.module.scss';

const NEW_CATEGORY = '__new_category__';

const toFiles = (value) => (Array.isArray(value) ? value : value ? [value] : []);

/**
 * Upload of other documents. `onSave(item, files)` resolves to true when the upload succeeded;
 * a new category typed here is created by the page before uploading (item.isNewCategory).
 */
const OtherDocumentCreateModal = ({
    open,
    onClose,
    onSave,
    t,
    direction,
    typeOptions = [],
    titles = [],
    defaultDocumentType = '',
    defaultTitleId = null,
    fileTransported,
    canCreateCategory = false,
    busy = false,
    transferCount = 0,
}) => {
    const fileInputRef = useRef(null);
    const [title, setTitle] = useState('');
    const [documentType, setDocumentType] = useState('');
    const [documentDate, setDocumentDate] = useState(() => new Date());
    const [files, setFiles] = useState([]);
    const [newCategoryName, setNewCategoryName] = useState('');
    const [newCategoryHidden, setNewCategoryHidden] = useState(false);
    const [newCategoryTitleId, setNewCategoryTitleId] = useState('');
    const [dragging, setDragging] = useState(false);
    const [submitted, setSubmitted] = useState(false);

    useEffect(() => {
        if (!open) return;
        setTitle('');
        setDocumentType(defaultDocumentType || '');
        setDocumentDate(new Date());
        setFiles(toFiles(fileTransported));
        setNewCategoryName('');
        setNewCategoryHidden(false);
        setNewCategoryTitleId(defaultTitleId || '');
        setSubmitted(false);
    }, [open, defaultDocumentType, defaultTitleId, fileTransported]);

    const categoryOptions = useMemo(() => {
        const options = typeOptions
            .filter((type) => type?.name)
            .map((type) => ({ value: type.name, label: type.name }));
        return canCreateCategory ? [...options, { value: NEW_CATEGORY, label: `+ ${t('AddCategory')}` }] : options;
    }, [typeOptions, canCreateCategory, t]);

    const titleOptions = useMemo(
        () => [{ value: '', label: t('DefaultSection') }, ...titles.map((item) => ({ value: item.id, label: item.name }))],
        [titles, t]
    );

    const isNewCategory = documentType === NEW_CATEGORY;
    const trimmedNewCategory = newCategoryName.trim();
    const categoryExists = isNewCategory && typeOptions.some(
        (type) => (type?.name || '').trim().toLowerCase() === trimmedNewCategory.toLowerCase()
    );
    const resolvedType = isNewCategory ? trimmedNewCategory : documentType;
    const isTransfer = transferCount > 0;
    const missing = {
        title: !title.trim(),
        type: !resolvedType,
        date: !documentDate,
        files: !isTransfer && files.length === 0,
    };
    const canSave = !Object.values(missing).some(Boolean) && !busy;

    const addFiles = (list) => {
        const picked = Array.from(list || []);
        if (picked.length) setFiles((prev) => [...prev, ...picked]);
    };

    const handleSave = async () => {
        setSubmitted(true);
        if (!canSave) return;
        const ok = await onSave(
            {
                title: title.trim(),
                documentType: resolvedType,
                documentDate,
                isNewCategory: isNewCategory && !categoryExists,
                hiddenFromClient: isNewCategory ? newCategoryHidden : false,
                titleId: isNewCategory ? (newCategoryTitleId || null) : null,
            },
            files
        );
        if (ok) onClose();
    };

    return (
        <XtramModal
            open={open}
            onClose={onClose}
            title={t('newDocument')}
            direction={direction}
            busy={busy}
            onKeyDown={(e) => {
                if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) handleSave();
            }}
            footer={(
                <>
                    <XtramButton variant="secondary" onClick={onClose} disabled={busy}>{t('close')}</XtramButton>
                    <XtramButton onClick={handleSave} disabled={busy} busy={busy}>{t('save')}</XtramButton>
                </>
            )}
        >
            <div className={styles.stack}>
                {isTransfer && (
                    <XtramNotice type="info">{t('OtherDocumentsTransferNotice', { count: transferCount })}</XtramNotice>
                )}
                <div className={styles.row}>
                    <XtramField label={t('Title')} required className={styles.grow} error={submitted && missing.title ? t('RequiredField') : null}>
                        <XtramInput
                            direction={direction}
                            autoFocus
                            value={title}
                            onChange={(e) => setTitle(e.target.value)}
                            placeholder={t('Title')}
                            error={submitted && missing.title}
                        />
                    </XtramField>
                    <XtramField label={t('Document Date')} required size="narrow" error={submitted && missing.date ? t('RequiredField') : null}>
                        <XtramDatePicker value={documentDate} onChange={setDocumentDate} error={submitted && missing.date} />
                    </XtramField>
                </div>

                <XtramField label={t('Document Type')} required size="full" error={submitted && missing.type ? t('RequiredField') : null}>
                    <XtramSelect
                        direction={direction}
                        options={categoryOptions}
                        value={categoryOptions.find((option) => option.value === documentType) || null}
                        onChange={(option) => setDocumentType(option?.value || '')}
                        placeholder={t('Document Type')}
                        error={submitted && missing.type}
                    />
                </XtramField>

                {isNewCategory && (
                    <div className={styles.addCard}>
                        <XtramField
                            label={t('DocumentTypeName')}
                            required
                            className={styles.grow}
                            hint={categoryExists ? t('OtherDocumentsCategoryExistsWillUse') : null}
                        >
                            <XtramInput
                                direction={direction}
                                autoFocus
                                value={newCategoryName}
                                onChange={(e) => setNewCategoryName(e.target.value)}
                                placeholder={t('DocumentTypeName')}
                            />
                        </XtramField>
                        {titles.length > 0 && (
                            <XtramField label={t('OtherDocumentsTitle')}>
                                <XtramSelect
                                    direction={direction}
                                    options={titleOptions}
                                    value={titleOptions.find((option) => option.value === newCategoryTitleId) || titleOptions[0]}
                                    onChange={(option) => setNewCategoryTitleId(option?.value || '')}
                                    disableClearable
                                />
                            </XtramField>
                        )}
                        <XtramCheckbox
                            checked={newCategoryHidden}
                            onChange={setNewCategoryHidden}
                            label={t('HideCategoryFromClient')}
                        />
                    </div>
                )}

                {!isTransfer && (
                    <XtramField label={t('uploadDocument')} required size="full" error={submitted && missing.files ? t('RequiredField') : null}>
                        <label
                            className={`${styles.dropZone} ${dragging ? styles.dragging : ''}`}
                            onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
                            onDragLeave={() => setDragging(false)}
                            onDrop={(e) => {
                                e.preventDefault();
                                setDragging(false);
                                addFiles(e.dataTransfer.files);
                            }}
                        >
                            <input
                                ref={fileInputRef}
                                type="file"
                                multiple
                                onChange={(e) => {
                                    addFiles(e.target.files);
                                    e.target.value = '';
                                }}
                            />
                            <CloudUploadOutlinedIcon className={styles.dropIcon} />
                            <span>{t('OtherDocumentsDropFiles')}</span>
                        </label>
                    </XtramField>
                )}

                {files.length > 0 && (
                    <ul className={styles.fileList}>
                        {files.map((file, index) => (
                            <li key={`${file.name}-${index}`} className={styles.fileChip} title={file.name}>
                                <span>{file.name}</span>
                                {!isTransfer && (
                                    <IconButton
                                        size="small"
                                        aria-label={t('Delete')}
                                        onClick={() => setFiles((prev) => prev.filter((_, i) => i !== index))}
                                    >
                                        <CloseIcon fontSize="inherit" />
                                    </IconButton>
                                )}
                            </li>
                        ))}
                    </ul>
                )}
            </div>
        </XtramModal>
    );
};

export default OtherDocumentCreateModal;
